import {OnboardingGuide, OnboardingTask} from './onboarding-guide.model';

export interface OnboardingNavigationTarget {
    selector: string;
    instruction: string;
    next?: string;
}

export interface OnboardingNavigationStep {
    title: string;
    waiting: string;
    targets: OnboardingNavigationTarget[];
}

export interface OnboardingNavigationPlan {
    destination: string;
    initialStep: string;
    steps: Record<string, OnboardingNavigationStep>;
}

export function onboardingNavigationPlan(task: OnboardingTask, guide: OnboardingGuide): OnboardingNavigationPlan {
    const target = (key: string, instruction: string, next?: string): OnboardingNavigationTarget => ({selector: `[data-onboarding="${key}"]`, instruction, next});
    const createSite = task.key === 'site' || !guide.site_id;
    const reports = task.key === 'report' && task.action.path === '/app/reports';
    const steps: Record<string, OnboardingNavigationStep> = {
        sites: {
            title: 'Create your site', waiting: 'Opening Sites…',
            targets: [target('site.create', 'Select Add Site to begin.', 'create')],
        },
        create: {
            title: 'Create your site', waiting: 'Opening the site form…',
            targets: [
                target('site.create.standard', 'Choose Add Site to use survey forms.', 'create'),
                target('site.create.legacy', 'Choose Add Legacy Site for a legacy survey.', 'create'),
                target('site.create.details', 'Start with the site name and client, then follow the form to save your site.'),
            ],
        },
        workspace: {
            title: task.key === 'forms' ? 'Choose survey forms' : task.key === 'report' ? 'Create your report' : 'Choose a building',
            waiting: 'Opening your site…', targets: [],
        },
        forms: {
            title: 'Choose survey forms', waiting: 'Opening Edit Site…',
            targets: [target('site.forms', 'Choose the forms your team needs, then save your changes.')],
        },
        floors: {
            title: 'Choose a floor', waiting: 'Loading the floors…',
            targets: [target('floor.open', 'Open a floor to continue.', 'floor')],
        },
        floor: {
            title: task.key === 'structure' ? 'Prepare your floor' : 'Add your inspection', waiting: 'Opening the floor…',
            targets: task.key === 'structure'
                ? [target('drawing.create', 'Add a drawing, or choose Survey without a floor plan.'), target('inspection.add', 'This floor is ready. Use Add Pin to record an inspection.')]
                : [target('inspection.add', 'Select Add Pin to record an inspection on this floor.'), target('drawing.open', 'Open the drawing you want to inspect.', 'drawing')],
        },
        drawing: {
            title: 'Add your inspection', waiting: 'Opening the drawing…',
            targets: [target('inspection.add', 'Select Add Pin, then choose the location on the plan.')],
        },
        reports: {
            title: 'Your reports', waiting: 'Opening Reports…',
            targets: [target('reports.list', 'Find your report here and use its download action to open it.')],
        },
    };
    if (task.key === 'forms') {
        steps.workspace.targets = [target('site.edit', 'Select Edit Site to choose your survey forms.', 'forms')];
    } else if (task.key === 'report') {
        steps.workspace.targets = [target('site.show.actions', 'Select Create Site Report to review the report options.')];
    } else {
        steps.workspace.targets = [target('building.open', 'Open a building to find its floors.', 'floors')];
        if (task.key === 'structure') {
            steps.workspace.targets.push(target('building.create', 'Create a building first, then open it to add a floor.'));
            steps.floors.targets.push(target('floor.create', 'Create a floor, then open it to add a drawing or inspection log.'));
        }
    }
    return {
        destination: reports ? '/app/reports' : createSite ? '/app/sites' : `/app/sites/${guide.site_id}`,
        initialStep: reports ? 'reports' : createSite ? 'sites' : 'workspace',
        steps,
    };
}
